import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import { ALL_COUNTRIES_WILDCARD } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { createAddonCallable } from '@/lib/callables';
import {
  subscribeAddonConfigs,
  subscribeMarketConfigs,
  type AddonConfigListResult,
  type MarketConfigListResult,
} from '@/lib/firestoreTenantDetail';
import {
  isAlreadyExistsError,
  parseModuleCatalogFile,
  planModuleCatalogImport,
  resolveImportCountries,
  type ImportPlanRow,
} from '@/features/billing/moduleCatalogImport';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { toast } from '@/lib/notifications';

type RowResult = 'CREATED' | 'SKIPPED' | 'FAILED';
type RunState =
  | { kind: 'IDLE' }
  | { kind: 'RUNNING'; results: Record<string, RowResult> }
  | { kind: 'DONE'; results: Record<string, RowResult> };

/**
 * #679 F3 — `/billing/addons/import`. Importa o catálogo de módulos de um
 * arquivo JSON versionado (`docs/catalog/modules-679.json`): prévia primeiro,
 * depois um `createAddon` por módulo novo, em sequência. SKU que já existe é
 * pulado e nunca sobrescrito — as diferenças ficam visíveis para edição manual.
 *
 * Spec: docs/screens/billing-addons-import.md.
 *
 * Origem: #710 — commit f44f75e85, 2026-09-24 13:31 +0200 (#679, importação do
 * catálogo).
 *
 * Contrato: assina `subscribeMarketConfigs` e `subscribeAddonConfigs` para
 * montar a prévia (`moduleCatalogImport`); "Executar" (após `AdminConfirm`)
 * chama `createAddon` um a um, em sequência, e marca cada linha como
 * criada, pulada ou com falha. Nenhuma escrita direta no Firestore.
 *
 * Usado por: rota `billing/addons/import` (`routes/router.tsx`), botão no
 * cabeçalho da `AddonsPage`.
 *
 * Dicas: `already-exists` (outra aba criou o SKU entre a prévia e a execução)
 * conta como pulado e é lido em `err.code`, nunca na mensagem; qualquer outro
 * erro marca a linha como falha e o lote continua. A guarda de reentrada no
 * início de `execute` impede criar o lote duas vezes com duplo clique.
 */
export function ModuleCatalogImportPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [markets, setMarkets] = useState<MarketConfigListResult>({ kind: 'LOADING' });
  const [addons, setAddons] = useState<AddonConfigListResult>({ kind: 'LOADING' });
  const [confirming, setConfirming] = useState(false);
  const [run, setRun] = useState<RunState>({ kind: 'IDLE' });

  useEffect(() => subscribeMarketConfigs(setMarkets), []);
  useEffect(() => subscribeAddonConfigs(setAddons), []);

  const parsed = useMemo(() => (text.trim() === '' ? null : parseModuleCatalogFile(text)), [text]);

  const countries = useMemo(
    () =>
      parsed?.kind === 'OK' && markets.kind === 'READY'
        ? resolveImportCountries(parsed.file.enabled_countries, markets.markets)
        : null,
    [parsed, markets],
  );

  // Durante e depois da execução a prévia fica congelada — linhas e países: o
  // snapshot de add-ons muda a cada criação (todo NEW viraria EXISTS) e o
  // cabeçalho tem de mostrar os países que foram de fato enviados.
  const [frozen, setFrozen] = useState<{ rows: ImportPlanRow[]; countries: string[] } | null>(null);
  const liveRows = useMemo(
    () =>
      parsed?.kind === 'OK' && addons.kind === 'READY' && countries !== null
        ? planModuleCatalogImport(parsed.file, addons.addons, countries)
        : null,
    [parsed, addons, countries],
  );
  const rows = frozen?.rows ?? liveRows;
  const shownCountries = frozen?.countries ?? countries;
  const newRows = rows?.filter((r) => r.status === 'NEW') ?? [];

  const canImport =
    run.kind === 'IDLE' && rows !== null && countries !== null && countries.length > 0 && newRows.length > 0;

  /** Volta ao estado inicial — é o "rodar de novo completa o resto" da spec. */
  function reset(): void {
    setRun({ kind: 'IDLE' });
    setFrozen(null);
    setFiles([]);
    setText('');
  }

  async function onFiles(next: File[]): Promise<void> {
    setFiles(next);
    const file = next[0];
    // Arquivo removido limpa o conteúdo: sem isso a prévia seguia mostrando o
    // arquivo que o operador acabou de tirar.
    setText(file ? await file.text() : '');
  }

  async function execute(): Promise<void> {
    // Reentrada (duplo clique no confirmar) criaria o lote duas vezes.
    if (!rows || countries === null || run.kind !== 'IDLE') return;
    setConfirming(false);
    setFrozen({ rows, countries });
    const results: Record<string, RowResult> = {};
    for (const row of rows) if (row.status === 'EXISTS') results[row.sku] = 'SKIPPED';
    setRun({ kind: 'RUNNING', results: { ...results } });
    for (const row of rows) {
      if (row.status !== 'NEW') continue;
      try {
        await createAddonCallable(row.input);
        results[row.sku] = 'CREATED';
      } catch (err) {
        // Outra aba criou o SKU entre a prévia e agora: é o mesmo "pulado".
        results[row.sku] = isAlreadyExistsError(err) ? 'SKIPPED' : 'FAILED';
        if (results[row.sku] === 'FAILED') {
          console.error(`createAddon ${row.sku} failed`, err);
          // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
          // Auditoria 2026-10-25 15:00 +0100 (#1021): o report saía também para o `already-exists`
          // (SKIPPED, corrida esperada com outra aba) e enchia o PostHog de falso erro. Só FAILED reporta.
          reportNonFatal('admin_module_catalog_import_page_failed', {
            site: 'createAddon failed',
            code: (err as { code?: string }).code ?? null,
            ...serializeError(err),
          });
        }
      }
      setRun({ kind: 'RUNNING', results: { ...results } });
    }
    setRun({ kind: 'DONE', results });
    // Auditoria 2026-10-25 14:29 +0100 (#1021): o resumo com falhas fica abaixo da tabela, e o Importar no
    // header dela; sem toast, quem disparou do topo não via que parte falhou.
    const failedCount = Object.values(results).filter((r) => r === 'FAILED').length;
    if (failedCount > 0) {
      const createdCount = Object.values(results).filter((r) => r === 'CREATED').length;
      toast.error(t('billing.addonsImport.summary', { created: createdCount, failed: failedCount }));
    }
  }

  const results = run.kind === 'IDLE' ? null : run.results;
  const failed = results ? Object.values(results).filter((r) => r === 'FAILED').length : 0;
  const created = results ? Object.values(results).filter((r) => r === 'CREATED').length : 0;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('billing.addonsImport.subtitle')}
          actions={
            <Button
              iconName="arrow-left"
              // Sair no meio do lote não o interrompe: as chamadas seguem, sem
              // ninguém vendo resultado (e criando Products na Stripe live).
              disabled={run.kind === 'RUNNING'}
              onClick={(): void => {
                void navigate('/billing/addons');
              }}
            >
              {t('billing.addonsImport.back')}
            </Button>
          }
        >
          {t('billing.addonsImport.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2">{t('billing.addonsImport.fileTitle')}</Header>}>
          <SpaceBetween size="m">
            {run.kind === 'IDLE' && (
              <FormField label={t('billing.addonsImport.fileLabel')} description={t('billing.addonsImport.fileHint')}>
                <FileUpload
                  value={files}
                  accept="application/json,.json"
                  onChange={({ detail }): void => {
                    void onFiles(detail.value);
                  }}
                  i18nStrings={{
                    uploadButtonText: () => t('billing.addonsImport.chooseFile'),
                    dropzoneText: () => t('billing.addonsImport.chooseFile'),
                    removeFileAriaLabel: () => t('billing.addonsImport.removeFile'),
                    limitShowFewer: '',
                    limitShowMore: '',
                    errorIconAriaLabel: 'Error',
                  }}
                />
              </FormField>
            )}
            <FormField label={t('billing.addonsImport.pasteLabel')}>
              <Textarea
                value={text}
                rows={6}
                disabled={run.kind !== 'IDLE'}
                ariaLabel={t('billing.addonsImport.pasteLabel')}
                onChange={({ detail }): void => {
                  setText(detail.value);
                }}
              />
            </FormField>
            {parsed?.kind === 'INVALID_JSON' && (
              <Alert type="error">{t('billing.addonsImport.errors.invalidJson')}</Alert>
            )}
            {parsed?.kind === 'INVALID_SCHEMA' && (
              <Alert type="error" header={t('billing.addonsImport.errors.invalidSchema')}>
                <SpaceBetween size="xxs">
                  {parsed.issues.slice(0, 10).map((issue) => (
                    <Box key={issue} fontSize="body-s">
                      {issue}
                    </Box>
                  ))}
                </SpaceBetween>
              </Alert>
            )}
            {(markets.kind === 'ERROR' || addons.kind === 'ERROR') && (
              <Alert type="error">{t('billing.addonsImport.errors.load')}</Alert>
            )}
            {countries !== null && countries.length === 0 && (
              <Alert type="error">{t('billing.addonsImport.errors.noCountries')}</Alert>
            )}
          </SpaceBetween>
        </Container>

        {rows !== null && (
          <Table
            header={
              <Header
                variant="h2"
                description={t('billing.addonsImport.countriesResolved', {
                  // #954: `["*"]` aparece como "Todos os países", não "*".
                  list:
                    shownCountries === null
                      ? '—'
                      : shownCountries.includes(ALL_COUNTRIES_WILDCARD)
                        ? t('billing.addons.allCountries')
                        : shownCountries.join(', '),
                })}
                actions={
                  run.kind === 'DONE' ? (
                    <Button onClick={reset}>{t('billing.addonsImport.restart')}</Button>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={!canImport}
                      loading={run.kind === 'RUNNING'}
                      onClick={(): void => {
                        setConfirming(true);
                      }}
                    >
                      {t('billing.addonsImport.import', { count: newRows.length })}
                    </Button>
                  )
                }
              >
                {t('billing.addonsImport.previewTitle')}
              </Header>
            }
            items={rows}
            trackBy="sku"
            variant="container"
            columnDefinitions={[
              {
                id: 'sku',
                header: t('billing.addonsImport.col.sku'),
                cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.sku}</span>,
              },
              {
                id: 'name',
                header: t('billing.addonsImport.col.name'),
                cell: (r) => r.input.name_i18n['pt-BR'],
              },
              {
                id: 'sellable',
                header: t('billing.addonsImport.col.sellable'),
                cell: (r) =>
                  r.sellable ? (
                    <StatusIndicator type="success">{t('billing.addons.sellableYes')}</StatusIndicator>
                  ) : (
                    <StatusIndicator type="stopped">{t('billing.addons.sellableNo')}</StatusIndicator>
                  ),
              },
              {
                id: 'status',
                header: t('billing.addonsImport.col.status'),
                cell: (r) => <RowStatus row={r} result={results?.[r.sku]} />,
              },
            ]}
          />
        )}

        {run.kind === 'DONE' && (
          <Alert type={failed > 0 ? 'error' : 'success'}>
            {t('billing.addonsImport.summary', { created, failed })}
          </Alert>
        )}
      </SpaceBetween>

      <AdminConfirm
        visible={confirming}
        title={t('billing.addonsImport.confirmTitle', { count: newRows.length })}
        confirmLabel={t('billing.addonsImport.confirm')}
        cancelLabel={t('billing.addons.dialog.cancel')}
        onConfirm={(): void => {
          void execute();
        }}
        onDismiss={(): void => {
          setConfirming(false);
        }}
      >
        <Box>{t('billing.addonsImport.confirmBody', { count: newRows.length, products: newRows.length * 2 })}</Box>
      </AdminConfirm>
    </ContentLayout>
  );
}

/** Indicador de uma linha: resultado da execução ou, antes dela, nova / igual / com diferença. */
function RowStatus({ row, result }: { row: ImportPlanRow; result: RowResult | undefined }): React.JSX.Element {
  const { t } = useTranslation();
  if (result === 'CREATED') return <StatusIndicator type="success">{t('billing.addonsImport.result.created')}</StatusIndicator>;
  if (result === 'FAILED') return <StatusIndicator type="error">{t('billing.addonsImport.result.failed')}</StatusIndicator>;
  if (row.status === 'NEW') {
    return result === undefined ? (
      <StatusIndicator type="pending">{t('billing.addonsImport.status.new')}</StatusIndicator>
    ) : (
      <StatusIndicator type="info">{t('billing.addonsImport.result.skipped')}</StatusIndicator>
    );
  }
  if (row.diff.length === 0) {
    return <StatusIndicator type="info">{t('billing.addonsImport.status.existsSame')}</StatusIndicator>;
  }
  return (
    <StatusIndicator type="warning">
      {t('billing.addonsImport.status.existsDiff', {
        fields: row.diff.map((f) => t(`billing.addonsImport.diff.${f}`)).join(', '),
      })}
    </StatusIndicator>
  );
}
